'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  industry: string;
  inquiryType: string;
  employeeCount: string;
  message: string;
}

interface FormErrors {
  [key: string]: string;
}

const industries = [
  'Banking & Financial Services',
  'Aviation',
  'Government & Public Sector',
  'Hospitality & Tourism',
  'Corporate & Business Services',
  'Retail & Commerce',
  'Healthcare',
  'Education',
  'Manufacturing',
  'Other'
];

const inquiryTypes = [
  'New Partnership Consultation',
  'RFP / Tender Inquiry',
  'Existing Client Support',
  'Vendor Application',
  'Distributor Partnership',
  'International Collaboration',
  'Facility Tour Request',
  'General Information'
];

const employeeCounts = [
  '1-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1000+ employees'
];

const ContactFormSection: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    inquiryType: '',
    employeeCount: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.industry) {
      newErrors.industry = 'Please select an industry';
    }

    if (!formData.inquiryType) {
      newErrors.inquiryType = 'Please select an inquiry type';
    }

    if (!formData.employeeCount) {
      newErrors.employeeCount = 'Please select employee count';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      industry: '',
      inquiryType: '',
      employeeCount: '',
      message: ''
    });

    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card rounded-lg shadow-lg p-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-12 bg-muted rounded"></div>
                ))}
              </div>
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded w-48"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-primary mb-4">
              Request Partnership Consultation
            </h2>
            <p className="text-lg text-text-secondary font-body">
              Complete the form below and our partnership team will respond within 24 hours with a customized proposal.
            </p>
          </div>

          {submitSuccess && (
            <div className="mb-8 bg-success bg-opacity-10 border border-success rounded-lg p-6 flex items-start space-x-4">
              <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-headline font-semibold text-success mb-2">
                  Consultation Request Received!
                </h3>
                <p className="text-sm text-text-secondary font-body">
                  Thank you for your interest in Pink Diamond Company Limited. Our partnership team will review your inquiry and contact you within 24 hours to schedule your complimentary consultation.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-card rounded-lg shadow-lg p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Full Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.fullName ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300`}
                  placeholder="John Doe"
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.fullName}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Email Address <span className="text-destructive">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.email ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300`}
                  placeholder="john.doe@company.co.tz"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Phone Number <span className="text-destructive">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.phone ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300`}
                  placeholder="+255 754 123 456"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Company Name <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.company ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300`}
                  placeholder="Your Company Ltd."
                />
                {errors.company && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.company}</p>
                )}
              </div>

              <div>
                <label htmlFor="industry" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Industry <span className="text-destructive">*</span>
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.industry ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white`}
                >
                  <option value="">Select Industry</option>
                  {industries.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
                {errors.industry && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.industry}</p>
                )}
              </div>

              <div>
                <label htmlFor="inquiryType" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Inquiry Type <span className="text-destructive">*</span>
                </label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.inquiryType ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white`}
                >
                  <option value="">Select Inquiry Type</option>
                  {inquiryTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.inquiryType && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.inquiryType}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="employeeCount" className="block text-sm font-body font-medium text-text-primary mb-2">
                  Number of Employees <span className="text-destructive">*</span>
                </label>
                <select
                  id="employeeCount"
                  name="employeeCount"
                  value={formData.employeeCount}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border ${errors.employeeCount ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 bg-white`}
                >
                  <option value="">Select Employee Count</option>
                  {employeeCounts.map(count => (
                    <option key={count} value={count}>{count}</option>
                  ))}
                </select>
                {errors.employeeCount && (
                  <p className="mt-1 text-sm text-destructive font-body">{errors.employeeCount}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-body font-medium text-text-primary mb-2">
                Message <span className="text-destructive">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-3 border ${errors.message ? 'border-destructive' : 'border-border'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 resize-none`}
                placeholder="Please provide details about your uniform requirements, timeline, and any specific needs..."
              ></textarea>
              {errors.message && (
                <p className="mt-1 text-sm text-destructive font-body">{errors.message}</p>
              )}
              <p className="mt-2 text-xs text-text-secondary font-body">
                Minimum 20 characters required
              </p>
            </div>

            <div className="flex items-start space-x-3 bg-muted bg-opacity-50 p-4 rounded-md">
              <Icon name="InformationCircleIcon" size={20} variant="solid" className="text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-text-secondary font-body">
                By submitting this form, you agree to be contacted by Pink Diamond Company Limited regarding your inquiry. We respect your privacy and will never share your information with third parties.
              </p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full md:w-auto px-8 py-4 bg-brand-gold text-brand-gold-foreground font-cta font-semibold rounded-md hover:bg-opacity-90 transition-all duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-brand-gold-foreground border-t-transparent rounded-full animate-spin"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Request Consultation</span>
                  <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactFormSection;